import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  FileText,
  Video,
  Bot,
  Workflow,
  Globe,
  Lightbulb,
  Cpu,
  Sparkles,
  Settings2,
  Utensils,
  Scale,
  Home,
  Stethoscope,
  ShoppingCart,
  Wrench,
  Zap,
  Lock,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { WhyUsSection } from "@/components/sections/WhyUsSection";
import type { GrowthItem } from "@/components/sections/WhyUsSection.data";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";

export const Route = createFileRoute("/ai-services")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "AI Services — Mind Media" },
      {
        name: "description",
        content:
          "AI content engines, video, voice, chatbots, and automations that give your team hours back, built on your brand voice with no hype.",
      },
      { property: "og:title", content: "AI Services — Mind Media" },
      {
        property: "og:description",
        content:
          "Put AI to work so your team stops doing busywork. Built on your brand, owned by you.",
      },
    ],
  }),
  component: AiServicesPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

const aiGrowthItems: GrowthItem[] = [
  {
    title: "Save hours every week",
    description: "Automations that quietly run the busywork while your team focuses on real work.",
    Icon: Zap,
    bgClass: "bg-[#3b82f6]/10",
    textClass: "text-[#3b82f6]",
  },
  {
    title: "Sounds like your brand",
    description: "Trained on your voice and business, so output looks human, not AI-generated.",
    Icon: Sparkles,
    bgClass: "bg-[#a855f7]/10",
    textClass: "text-[#a855f7]",
  },
  {
    title: "You own everything",
    description: "Built under your accounts. Your data, your workflows, your leverage.",
    Icon: Lock,
    bgClass: "bg-[#ec4899]/10",
    textClass: "text-[#ec4899]",
  },
];

function AiServicesPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });
  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-4 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#contact" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#contact"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Put AI to work so your team stops doing <span className={gradText}>busywork.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-5xl text-lg text-muted-foreground sm:text-xl">
            Most agencies sell you a chatbot and call it AI. We build the content engines, video,
            voice, and automations that actually give you hours back, and we make them look and
            sound like your brand.
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              See what AI can do for you <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#services"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Explore services
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {["Built on your brand voice", "You own everything", "No hype, just hours saved"].map(
              (p) => (
                <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                    <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                  </span>
                  {p}
                </span>
              ),
            )}
          </div>
        </div>
        <div className="relative">
          <WhyUsSection
            onCtaClick={goToMockup}
            variant="hero"
            showTrustBar={false}
            showRealExperience={false}
            withDivider={false}
            shareloId="z9wadYaQmAFIEmpwtslOp"
            shareloTitle="AI services — customer story"
            headline={
              <>
                <span className="block sm:inline">What great</span>{" "}
                <span className="block sm:inline">
                  <span className="inline-flex items-baseline whitespace-nowrap sm:inline">
                    AI
                    <span className="inline sm:hidden">{"\u00A0"}</span>
                    <span className="hidden sm:inline"> </span>
                    <span className="text-gradient-cycle align-baseline">actually does</span>
                  </span>
                </span>
              </>
            }
            growthItems={aiGrowthItems}
          />
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What we build"
            title={
              <>
                AI that solves <span className={gradText}>real business problems.</span>
              </>
            }
          >
            Not a demo that impresses you for five minutes. Working systems that quietly{" "}
            <br className="hidden md:inline" />
            run in the background and save your team real time every week.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: FileText,
                h: "AI Content Engines",
                p: "Blog posts, product descriptions, and social content drafted in your brand voice, on a schedule, so the content machine never stalls.",
                b: ["Brand-voice trained", "Blog, social & product copy", "Human review built in"],
              },
              {
                icon: Video,
                h: "AI Video & Voice",
                p: "Short-form ads, UGC-style clips, product videos, and voiceovers generated fast, so you can test ten creatives in the time it took to make one.",
                b: ["Ad & UGC-style video", "AI voiceovers & avatars", "On-brand, every time"],
              },
              {
                icon: Bot,
                h: "Custom Chatbots & Assistants",
                p: "Site assistants trained on your real business that answer questions, qualify leads, and book calls 24/7, and actually sound like you.",
                b: ["Trained on your data", "Captures & qualifies leads", "Works on any website"],
              },
              {
                icon: Workflow,
                h: "Workflow Automation",
                p: "Connect your tools and let the repetitive work run itself. Email replies, lead routing, invoicing, reporting, handled without you touching it.",
                b: ["Multi-tool pipelines", "CRM & inbox automation", "Runs while you sleep"],
              },
              {
                icon: Globe,
                h: "AI-Powered Websites",
                p: "Sites with intelligence built in: smart search, dynamic FAQs, personalized content, and a chat that feels human, not robotic.",
                b: ["Smart search & FAQs", "Personalized content", "Fast & conversion-focused"],
              },
              {
                icon: Lightbulb,
                h: "AI Strategy & Setup",
                p: "Not sure where AI fits? We map the highest-ROI uses for your specific business, pick the right tools, and train your team to run them.",
                b: ["ROI-first roadmap", "Tool selection", "Team training workshops"],
              },
            ].map(({ icon: Icon, h, p, b }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
                <ul className="mt-4 space-y-2">
                  {b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORMS */}
      <section id="platforms" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="Our toolkit"
            title={
              <span className="lg:whitespace-nowrap">
                We use the AI tools that <span className={gradText}>actually ship work.</span>
              </span>
            }
          >
            The landscape changes every week. We stay on top of it so you don't have to, with
            hands-on <br className="hidden md:inline" />
            experience across the models and tools that matter, not just whatever's trending.
          </SectionHead>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <ul className="space-y-5">
                {[
                  {
                    icon: Cpu,
                    title: "The major models",
                    body: "ChatGPT, Claude, and Gemini for content, support, analysis, and assistants. We pick the right one for the job, not the trendy one.",
                  },
                  {
                    icon: Sparkles,
                    title: "Creative AI",
                    body: "Video, voice, and image generation tools that produce ad and content creative fast, kept on-brand so it doesn't look AI-made.",
                  },
                  {
                    icon: Settings2,
                    title: "Automation & builders",
                    body: "Workflow automation and rapid app builders that wire your tools together and turn ideas into working systems in days, not months.",
                  },
                ].map(({ icon: Icon, title, body }) => (
                  <li key={title} className="flex gap-4">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                      <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                    </span>
                    <div>
                      <strong className="block text-base font-bold">{title}</strong>
                      <span className="text-[15px] text-muted-foreground">{body}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <div className="text-6xl tracking-tight leading-none font-bold text-white">3am</div>
              <div className="mt-2 text-lg font-bold">on a Sunday, still working</div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                The whole point of AI done right: it doesn't clock out. Your assistant answers
                customers, your automations route leads, and your content keeps shipping while
                you're asleep, on vacation, or busy doing the work only you can do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="usecases" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="Real use cases"
            title={
              <>
                How businesses put this <span className={gradText}>to work.</span>
              </>
            }
          >
            AI isn't one-size-fits-all. Here's what it looks like across the businesses we work with
            most.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Utensils,
                h: "Restaurants",
                p: "Assistants that handle reservations, menu questions, and catering inquiries automatically, day and night.",
              },
              {
                icon: Scale,
                h: "Law Firms",
                p: "AI intake that qualifies leads and books consultations before a human ever picks up the phone.",
              },
              {
                icon: Home,
                h: "Real Estate",
                p: "Property bots that answer listing questions, schedule showings, and collect buyer info around the clock.",
              },
              {
                icon: Stethoscope,
                h: "Medical Offices",
                p: "Patient-facing assistants for appointment scheduling, insurance questions, and office-hours inquiries.",
              },
              {
                icon: ShoppingCart,
                h: "E-Commerce",
                p: "Product recommendations, automated inventory alerts, and support bots that handle tracking and returns.",
              },
              {
                icon: Wrench,
                h: "Service Businesses",
                p: "Smart bots for contractors and consultants that qualify leads, send quotes, and book jobs hands-free.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <span className="lg:whitespace-nowrap">
                From idea to running system in <span className={gradText}>weeks, not months.</span>
              </span>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: 1,
                h: "Find the bottleneck",
                p: "We look at where your time and money leak, and pick the use case with the fastest, clearest payoff.",
              },
              {
                n: 2,
                h: "Build it on your brand",
                p: "We train the system on your real business and voice so the output sounds like you, not a generic bot.",
              },
              {
                n: 3,
                h: "Launch & connect",
                p: "We plug it into your site and tools, test it in the real world, and tune it until it's reliable.",
              },
              {
                n: 4,
                h: "Train your team",
                p: "We hand it over with plain-English training so your team runs it confidently, and you own all of it.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn(
                    "flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue",
                  )}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection className="pt-4 sm:pt-2" hideMockupButton meetingButtonPrimary />

      {/* FAQ */}
      <section id="faq" className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="Questions"
            title={
              <span className="lg:whitespace-nowrap">
                The honest answers <span className={gradText}>people want.</span>
              </span>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "Is AI going to replace my team?",
                "No. We use it to kill the busywork, the repetitive drafting, sorting, and answering, so your people spend their time on the work that actually needs a human. It's leverage, not a layoff.",
              ],
              [
                "Is my business data safe?",
                "We set things up so your data stays yours and isn't used to train public models. We'll walk you through exactly what's stored, where, and who can see it before anything goes live.",
              ],
              [
                "How fast can this be set up?",
                "Most projects go from idea to a working system in a few weeks, depending on scope. A simple site assistant is fast. A full content engine or multi-tool automation takes a bit longer.",
              ],
              [
                "Won't AI content sound generic?",
                "It does if you let the tool run raw. We train it on your brand voice and keep a human review step, so what goes out sounds like you wrote it, not like a robot filled a template.",
              ],
              [
                "Which AI do you use?",
                "Whatever fits the job. We work across the major models and creative tools and pick based on what gets you the best result, not on what we're locked into.",
              ],
              [
                "Do I own what you build?",
                "Yes. Every account, workflow, and asset is yours. We set it up under your accounts so you're never held hostage if we part ways.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <ContactSection
        compact
        ctaText="Tell us what you do"
        heading={
          <h2 className="text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-[2.65rem] lg:text-[2.75rem]">
            Curious how AI
            <br />
            <span className="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent sm:whitespace-nowrap">
              could help your business?
            </span>
          </h2>
        }
        subheading={
          <p className="mt-6 text-lg text-muted-foreground">
            Tell us what you do and we'll send back the three best ways AI can save you time. Free,
            no commitment.
          </p>
        }
      />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/seo", label: "SEO" },
          { href: "/ai-services", label: "AI Services" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
